// app/screens/professional/EditProfileScreen.js
// JobEng - Comprehensive Profile Editor
//
// Full rebuild: 7 sections (Basic | Professional | Experience | Education
// | Skills | Social | Preferences), all data flows through ProfileManagementService
// so edits propagate to ProfileScreen, ResumePreviewScreen, BuildResumeScreen, etc.
//
// Replaces the previous 404-line basic editor. Backed by the unified data spine.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  Modal,
  Image,
  Switch,
  Pressable,
  SafeAreaView,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// Optional icon import with safe fallback
const FallbackIcon = ({ name, size = 24, color = '#666' }) => (
  <View style={{ width: size, height: size, backgroundColor: color, borderRadius: size / 2, opacity: 0.3 }} />
);
let MaterialCommunityIcons = FallbackIcon;
let Ionicons = FallbackIcon;
try {
  const icons = require('@expo/vector-icons');
  if (icons?.MaterialCommunityIcons) MaterialCommunityIcons = icons.MaterialCommunityIcons;
  if (icons?.Ionicons) Ionicons = icons.Ionicons;
} catch {}

let LinearGradient = ({ children, style, ...rest }) => <View style={style} {...rest}>{children}</View>;
try {
  const lg = require('expo-linear-gradient');
  if (lg?.LinearGradient) LinearGradient = lg.LinearGradient;
} catch {}

const { width: SCREEN_W } = Dimensions.get('window');

// ──────────────────────────────────────────────────────────────────────────────
// Theme
// ──────────────────────────────────────────────────────────────────────────────
const COLORS = {
  primary:   '#0e4e96',
  primaryDk: '#0a3d75',
  accent:    '#7C3AED',
  success:   '#10B981',
  warning:   '#F59E0B',
  danger:    '#EF4444',
  info:      '#06B6D4',
  text:      '#1F2937',
  sub:       '#6B7280',
  light:     '#9CA3AF',
  bg:        '#F3F4F6',
  bgAlt:     '#F9FAFB',
  surface:   '#FFFFFF',
  border:    '#E5E7EB',
};

// ──────────────────────────────────────────────────────────────────────────────
// Constants — option lists
// ──────────────────────────────────────────────────────────────────────────────
const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Education', 'Construction', 'Retail', 'Manufacturing', 'Consulting', 'Media', 'Government', 'Non-profit', 'Hospitality', 'Real Estate', 'Energy', 'Transportation', 'Other'];
const EXPERIENCE_LEVELS = ['Student', 'Entry', 'Junior', 'Mid', 'Senior', 'Lead', 'Manager', 'Director', 'Executive'];
const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Volunteer'];
const WORK_PREFERENCES = ['On-site', 'Remote', 'Hybrid', 'Flexible'];
const PROFICIENCY_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert', 'Native'];
const NOTICE_PERIODS = ['Immediately', '1 week', '2 weeks', '1 month', '2 months', '3+ months'];
const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
const VISIBILITY_LEVELS = ['Public', 'Network only', 'Recruiters only', 'Private'];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
const EditProfileScreen = ({ navigation, route }) => {
  const existingProfile = route?.params?.profile || {};

  // ── Section state ──────────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [showExpModal, setShowExpModal] = useState(false);
  const [showEduModal, setShowEduModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [editingEdu, setEditingEdu] = useState(null);
  const [editingCert, setEditingCert] = useState(null);

  // ── Form state — comprehensive profile ─────────────────────────────────────
  const [profile, setProfile] = useState({
    // Basic
    firstName: '', lastName: '', email: '', phone: '', location: '',
    dateOfBirth: '', gender: '', pronouns: '', nationality: '', profilePicture: '',
    coverPhoto: '',

    // Professional
    headline: '', summary: '', jobTitle: '', company: '', industry: '',
    yearsExperience: '', experienceLevel: '',

    // Skills & languages
    skills: [],          // [{ name, level, years }]
    languages: [],       // [{ name, level }]
    interests: [],

    // Experience & education
    experiences: [],     // [{ title, company, location, startDate, endDate, current, description }]
    educations: [],      // [{ degree, institution, field, startDate, endDate, gpa, description }]
    certifications: [],  // [{ name, issuer, issueDate, expiryDate, credentialId }]

    // Social
    website: '', linkedin: '', github: '', twitter: '', instagram: '', youtube: '', behance: '', portfolio: '',

    // Job preferences
    openToWork: false,
    jobTypes: [],         // multi-select
    workPreferences: [],  // multi-select
    expectedSalaryMin: '', expectedSalaryMax: '', salaryCurrency: 'GBP',
    noticePeriod: '',
    willingToRelocate: false,
    willingToTravel: false,
    drivingLicence: false,
    workAuthorization: '',
    availability: '',

    // Privacy
    profileVisibility: 'Public',
    showEmail: false,
    showPhone: false,
    allowRecruiterContact: true,
    allowMessages: true,
  });

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);

      // 1. Try the service first
      let serviceData = null;
      try {
        await ProfileManagementService.bootstrap();
        const r = await ProfileManagementService.getProfile();
        if (r.success && r.data) serviceData = r.data;
      } catch {}

      // 2. Fall back to AsyncStorage cache
      let cachedData = null;
      try {
        const raw =
          (await AsyncStorage.getItem('userProfile')) ||
          (await AsyncStorage.getItem('prof_user_profile')) ||
          (await AsyncStorage.getItem('professional_default_userProfile'));
        if (raw) cachedData = JSON.parse(raw);
      } catch {}

      const source = serviceData || cachedData || {};
      const pi = source.personalInfo || source;

      // Map both unified-shape and legacy-shape fields
      setProfile(prev => ({
        ...prev,
        ...existingProfile,
        firstName:   pi.firstName || (source.fullName || pi.fullName || '').split(' ')[0] || existingProfile.firstName || '',
        lastName:    pi.lastName  || (source.fullName || pi.fullName || '').split(' ').slice(1).join(' ') || existingProfile.lastName || '',
        email:       pi.email    || source.email    || existingProfile.email    || '',
        phone:       pi.phone    || source.phone    || existingProfile.phone    || '',
        location:    pi.location || source.location || existingProfile.location || '',
        dateOfBirth: pi.dateOfBirth || pi.date_of_birth || existingProfile.dateOfBirth || '',
        gender:      pi.gender || existingProfile.gender || '',
        pronouns:    pi.pronouns || existingProfile.pronouns || '',
        nationality: pi.nationality || existingProfile.nationality || '',
        profilePicture: pi.profilePicture || pi.profile_image || pi.avatar || existingProfile.profilePicture || '',
        coverPhoto:  pi.coverPhoto || pi.cover_photo || existingProfile.coverPhoto || '',

        headline:    pi.headline || source.profession || pi.jobTitle || pi.job_title || existingProfile.headline || '',
        summary:     source.summary || source.professionalSummary || pi.about || pi.bio || existingProfile.summary || existingProfile.bio || '',
        jobTitle:    source.profession || pi.jobTitle || pi.job_title || existingProfile.jobTitle || '',
        company:     pi.company || existingProfile.company || '',
        industry:    pi.industry || existingProfile.industry || '',
        yearsExperience: pi.yearsExperience || pi.years_experience || existingProfile.yearsExperience || '',
        experienceLevel: pi.experienceLevel || existingProfile.experienceLevel || '',

        skills:         Array.isArray(source.skills) ? source.skills.map(normalizeSkill) : [],
        languages:      Array.isArray(source.languages) ? source.languages.map(normalizeLanguage) : [],
        interests:      Array.isArray(source.interests) ? source.interests : (pi.interests || []),
        experiences:    Array.isArray(source.experience) ? source.experience.map(normalizeExperience) : [],
        educations:     Array.isArray(source.education) ? source.education.map(normalizeEducation) : [],
        certifications: Array.isArray(source.certifications) ? source.certifications : [],

        website:   pi.website   || existingProfile.website   || '',
        linkedin:  pi.linkedin  || existingProfile.linkedin  || '',
        github:    pi.github    || existingProfile.github    || '',
        twitter:   pi.twitter   || existingProfile.twitter   || '',
        instagram: pi.instagram || existingProfile.instagram || '',
        youtube:   pi.youtube   || existingProfile.youtube   || '',
        behance:   pi.behance   || existingProfile.behance   || '',
        portfolio: pi.portfolio || existingProfile.portfolio || '',

        openToWork:        source.openToWork ?? existingProfile.openToWork ?? false,
        jobTypes:          Array.isArray(source.jobTypes) ? source.jobTypes : [],
        workPreferences:   Array.isArray(source.workPreferences) ? source.workPreferences : [],
        expectedSalaryMin: source.expectedSalaryMin || existingProfile.expectedSalaryMin || '',
        expectedSalaryMax: source.expectedSalaryMax || existingProfile.expectedSalaryMax || '',
        salaryCurrency:    source.salaryCurrency || 'GBP',
        noticePeriod:      source.noticePeriod || existingProfile.noticePeriod || '',
        willingToRelocate: source.willingToRelocate ?? false,
        willingToTravel:   source.willingToTravel   ?? false,
        drivingLicence:    source.drivingLicence    ?? false,
        workAuthorization: source.workAuthorization || existingProfile.workAuthorization || '',
        availability:      source.availability || existingProfile.availability || '',

        profileVisibility:     source.profileVisibility || 'Public',
        showEmail:             source.showEmail ?? false,
        showPhone:             source.showPhone ?? false,
        allowRecruiterContact: source.allowRecruiterContact ?? true,
        allowMessages:         source.allowMessages ?? true,
      }));
    } catch (e) {
      console.error('loadProfile:', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Normalizers (handle both unified and legacy data shapes) ───────────────
  const normalizeSkill = (s) =>
    typeof s === 'string'
      ? { name: s, level: 'Intermediate', years: '' }
      : { name: s.name || s.skill_name || '', level: s.level || s.proficiency_level || 'Intermediate', years: s.years || s.years_experience || '' };

  const normalizeLanguage = (l) =>
    typeof l === 'string'
      ? { name: l, level: 'Intermediate' }
      : { name: l.name || l.language || '', level: l.level || l.proficiency || 'Intermediate' };

  const normalizeExperience = (e) => ({
    id: e.id || Date.now() + Math.random(),
    title: e.title || e.position || '',
    company: e.company || '',
    location: e.location || '',
    startDate: e.startDate || e.start_date || '',
    endDate: e.endDate || e.end_date || '',
    current: e.current || !e.endDate,
    description: e.description || e.summary || '',
  });

  const normalizeEducation = (e) => ({
    id: e.id || Date.now() + Math.random(),
    degree: e.degree || '',
    institution: e.institution || e.school || '',
    field: e.field || e.fieldOfStudy || e.field_of_study || '',
    startDate: e.startDate || e.start_date || '',
    endDate: e.endDate || e.end_date || '',
    gpa: e.gpa || e.grade || '',
    description: e.description || '',
  });

  // ── Update helper ──────────────────────────────────────────────────────────
  const update = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const toggleArrayValue = (field, value) => {
    setProfile(prev => {
      const arr = prev[field] || [];
      const has = arr.includes(value);
      return { ...prev, [field]: has ? arr.filter(v => v !== value) : [...arr, value] };
    });
    setDirty(true);
  };

  // ── Skills + Languages + Interests management ──────────────────────────────
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newInterest, setNewInterest] = useState('');

  const addSkill = () => {
    const name = newSkill.trim();
    if (!name) return;
    if (profile.skills.find(s => String(s.name ?? '').toLowerCase() === name.toLowerCase())) return;
    update('skills', [...profile.skills, { name, level: 'Intermediate', years: '' }]);
    setNewSkill('');
  };
  const updateSkillLevel = (idx, level) => {
    update('skills', profile.skills.map((s, i) => i === idx ? { ...s, level } : s));
  };
  const removeSkill = (idx) => update('skills', profile.skills.filter((_, i) => i !== idx));

  const addLanguage = () => {
    const name = newLanguage.trim();
    if (!name) return;
    if (profile.languages.find(l => String(l.name ?? '').toLowerCase() === name.toLowerCase())) return;
    update('languages', [...profile.languages, { name, level: 'Intermediate' }]);
    setNewLanguage('');
  };
  const updateLanguageLevel = (idx, level) => {
    update('languages', profile.languages.map((l, i) => i === idx ? { ...l, level } : l));
  };
  const removeLanguage = (idx) => update('languages', profile.languages.filter((_, i) => i !== idx));

  const addInterest = () => {
    const v = newInterest.trim();
    if (!v || profile.interests.includes(v)) return;
    update('interests', [...profile.interests, v]);
    setNewInterest('');
  };
  const removeInterest = (v) => update('interests', profile.interests.filter(i => i !== v));

  // ── Experience CRUD ────────────────────────────────────────────────────────
  const openAddExperience = () => {
    setEditingExp({ title: '', company: '', location: '', startDate: '', endDate: '', current: false, description: '' });
    setShowExpModal(true);
  };
  const openEditExperience = (exp) => { setEditingExp({ ...exp }); setShowExpModal(true); };
  const saveExperience = () => {
    if (!editingExp.title?.trim()) { Alert.alert('Required', 'Job title is required'); return; }
    if (editingExp.id && profile.experiences.find(e => e.id === editingExp.id)) {
      update('experiences', profile.experiences.map(e => e.id === editingExp.id ? editingExp : e));
    } else {
      update('experiences', [{ ...editingExp, id: Date.now() }, ...profile.experiences]);
    }
    setShowExpModal(false);
    setEditingExp(null);
  };
  const deleteExperience = (id) => {
    Alert.alert('Delete?', 'Remove this experience?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => update('experiences', profile.experiences.filter(e => e.id !== id)) },
    ]);
  };

  // ── Education CRUD ─────────────────────────────────────────────────────────
  const openAddEducation = () => {
    setEditingEdu({ degree: '', institution: '', field: '', startDate: '', endDate: '', gpa: '', description: '' });
    setShowEduModal(true);
  };
  const openEditEducation = (edu) => { setEditingEdu({ ...edu }); setShowEduModal(true); };
  const saveEducation = () => {
    if (!editingEdu.institution?.trim()) { Alert.alert('Required', 'Institution is required'); return; }
    if (editingEdu.id && profile.educations.find(e => e.id === editingEdu.id)) {
      update('educations', profile.educations.map(e => e.id === editingEdu.id ? editingEdu : e));
    } else {
      update('educations', [{ ...editingEdu, id: Date.now() }, ...profile.educations]);
    }
    setShowEduModal(false);
    setEditingEdu(null);
  };
  const deleteEducation = (id) => {
    Alert.alert('Delete?', 'Remove this education?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => update('educations', profile.educations.filter(e => e.id !== id)) },
    ]);
  };

  // ── Certifications CRUD ────────────────────────────────────────────────────
  const openAddCert = () => {
    setEditingCert({ name: '', issuer: '', issueDate: '', expiryDate: '', credentialId: '' });
    setShowCertModal(true);
  };
  const openEditCert = (cert) => { setEditingCert({ ...cert }); setShowCertModal(true); };
  const saveCert = () => {
    if (!editingCert.name?.trim()) { Alert.alert('Required', 'Certification name is required'); return; }
    if (editingCert.id && profile.certifications.find(c => c.id === editingCert.id)) {
      update('certifications', profile.certifications.map(c => c.id === editingCert.id ? editingCert : c));
    } else {
      update('certifications', [{ ...editingCert, id: Date.now() }, ...profile.certifications]);
    }
    setShowCertModal(false);
    setEditingCert(null);
  };
  const deleteCert = (id) => {
    Alert.alert('Delete?', 'Remove this certification?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => update('certifications', profile.certifications.filter(c => c.id !== id)) },
    ]);
  };

  // ── Profile completion percentage ──────────────────────────────────────────
  const completion = useMemo(() => {
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'location', 'headline', 'summary', 'jobTitle'];
    const arrayFields = ['skills', 'experiences', 'educations'];
    let score = 0;
    let total = requiredFields.length + arrayFields.length;
    requiredFields.forEach(f => { if (profile[f]) score += 1; });
    arrayFields.forEach(f => { if (Array.isArray(profile[f]) && profile[f].length > 0) score += 1; });
    return Math.round((score / total) * 100);
  }, [profile]);

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const fullName = `${profile.firstName} ${profile.lastName}`.trim();

      // Build unified shape for ProfileManagementService
      const unifiedPayload = {
        personalInfo: {
          firstName: profile.firstName,
          lastName: profile.lastName,
          fullName,
          name: fullName,
          email: profile.email,
          phone: profile.phone,
          location: profile.location,
          dateOfBirth: profile.dateOfBirth,
          gender: profile.gender,
          pronouns: profile.pronouns,
          nationality: profile.nationality,
          profilePicture: profile.profilePicture,
          coverPhoto: profile.coverPhoto,
          headline: profile.headline,
          jobTitle: profile.jobTitle,
          company: profile.company,
          industry: profile.industry,
          yearsExperience: profile.yearsExperience,
          experienceLevel: profile.experienceLevel,
          website: profile.website,
          linkedin: profile.linkedin,
          github: profile.github,
          twitter: profile.twitter,
          instagram: profile.instagram,
          youtube: profile.youtube,
          behance: profile.behance,
          portfolio: profile.portfolio,
        },
        profession:           profile.jobTitle || profile.headline,
        summary:              profile.summary,
        professionalSummary:  profile.summary,
        skills:               profile.skills,
        languages:            profile.languages,
        interests:            profile.interests,
        experience:           profile.experiences,
        education:            profile.educations,
        certifications:       profile.certifications,
        // Preferences + privacy
        openToWork:           profile.openToWork,
        jobTypes:             profile.jobTypes,
        workPreferences:      profile.workPreferences,
        expectedSalaryMin:    profile.expectedSalaryMin,
        expectedSalaryMax:    profile.expectedSalaryMax,
        salaryCurrency:       profile.salaryCurrency,
        noticePeriod:         profile.noticePeriod,
        willingToRelocate:    profile.willingToRelocate,
        willingToTravel:      profile.willingToTravel,
        drivingLicence:       profile.drivingLicence,
        workAuthorization:    profile.workAuthorization,
        availability:         profile.availability,
        profileVisibility:    profile.profileVisibility,
        showEmail:            profile.showEmail,
        showPhone:            profile.showPhone,
        allowRecruiterContact: profile.allowRecruiterContact,
        allowMessages:        profile.allowMessages,
        // Stats (preserved if existed)
        profileViews:         existingProfile.profileViews || 0,
        searchAppearances:    existingProfile.searchAppearances || 0,
        connections:          existingProfile.connections || 0,
        profileStrength:      completion,
        lastUpdated:          new Date().toISOString(),
      };

      // Save through service (also mirrors to all the AsyncStorage keys
      // ProfileScreen / Dashboard / Login / Resume screens read from)
      let serviceOk = false;
      try {
        const r = await ProfileManagementService.saveProfile(unifiedPayload);
        serviceOk = !!r?.success;
      } catch {}

      // Always also write the legacy keys so older screens keep working
      const legacyShape = {
        ...unifiedPayload.personalInfo,
        fullName,
        bio: profile.summary,
        about: profile.summary,
        summary: profile.summary,
        skills: profile.skills,
        experience: profile.experiences,
        education: profile.educations,
        certifications: profile.certifications,
        lastUpdated: unifiedPayload.lastUpdated,
        profileStrength: completion,
      };
      const json = JSON.stringify(legacyShape);
      await Promise.all([
        AsyncStorage.setItem('userProfile', json),
        AsyncStorage.setItem('prof_user_profile', json),
        AsyncStorage.setItem('professional_default_userProfile', json),
      ]);

      setDirty(false);
      Alert.alert(
        serviceOk ? 'Saved' : 'Saved locally',
        serviceOk
          ? 'Your profile has been updated and synced.'
          : 'Saved on this device. Backend sync will retry when reachable.',
        [
          { text: 'Stay Here', style: 'cancel' },
          { text: 'View Profile', onPress: () => navigation?.navigate('Profile') },
        ]
      );
    } catch (e) {
      Alert.alert('Save Error', e.message || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (dirty) {
      Alert.alert('Unsaved Changes', 'You have unsaved changes. Save before leaving?', [
        { text: 'Discard', style: 'destructive', onPress: () => navigation?.goBack() },
        { text: 'Cancel', style: 'cancel' },
        { text: 'Save', onPress: async () => { await handleSave(); navigation?.goBack(); } },
      ]);
    } else {
      navigation?.goBack();
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Section renderers
  // ══════════════════════════════════════════════════════════════════════════
  const renderBasic = () => (
    <>
      <SectionTitle>Photo</SectionTitle>
      <View style={s.photoRow}>
        <View style={s.profilePhoto}>
          {profile.profilePicture ? (
            <Image source={{ uri: profile.profilePicture }} style={{ width: '100%', height: '100%', borderRadius: 50 }} />
          ) : (
            <MaterialCommunityIcons name="account" size={48} color={COLORS.light} />
          )}
        </View>
        <View style={{ flex: 1 }}>
          <Field label="Photo URL" value={profile.profilePicture} onChange={(v) => update('profilePicture', v)} placeholder="https://..." autoCapitalize="none" />
          <Text style={s.helper}>Paste a public image URL. File upload coming soon.</Text>
        </View>
      </View>

      <SectionTitle style={{ marginTop: 16 }}>Personal Details</SectionTitle>
      <View style={s.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="First Name" required value={profile.firstName} onChange={(v) => update('firstName', v)} placeholder="First name" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Last Name" required value={profile.lastName} onChange={(v) => update('lastName', v)} placeholder="Last name" />
        </View>
      </View>

      <Field label="Email" required value={profile.email} onChange={(v) => update('email', v)} placeholder="you@example.com" keyboardType="email-address" autoCapitalize="none" />
      <Field label="Phone" value={profile.phone} onChange={(v) => update('phone', v)} placeholder="+44 ..." keyboardType="phone-pad" />
      <Field label="Location" value={profile.location} onChange={(v) => update('location', v)} placeholder="City, Country" />
      <Field label="Date of Birth" value={profile.dateOfBirth} onChange={(v) => update('dateOfBirth', v)} placeholder="YYYY-MM-DD" />

      <Label>Gender</Label>
      <ChipRow items={GENDERS} selected={profile.gender} onSelect={(v) => update('gender', v)} color={COLORS.primary} />

      <Field label="Pronouns" value={profile.pronouns} onChange={(v) => update('pronouns', v)} placeholder="e.g. she/her, they/them" />
      <Field label="Nationality" value={profile.nationality} onChange={(v) => update('nationality', v)} placeholder="e.g. British" />
    </>
  );

  const renderProfessional = () => (
    <>
      <SectionTitle>Professional Profile</SectionTitle>
      <Field
        label="Headline"
        value={profile.headline}
        onChange={(v) => update('headline', v)}
        placeholder="e.g. Senior React Native Developer at Acme"
        maxLength={120}
      />
      <Text style={s.helper}>{(profile.headline || '').length} / 120 — appears as your tagline</Text>

      <Field
        label="Summary / About"
        value={profile.summary}
        onChange={(v) => update('summary', v)}
        placeholder="A few paragraphs about your professional background, goals, and what you're looking for..."
        multiline
        rows={6}
      />

      <View style={s.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Current Job Title" value={profile.jobTitle} onChange={(v) => update('jobTitle', v)} placeholder="e.g. Software Engineer" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Current Company" value={profile.company} onChange={(v) => update('company', v)} placeholder="Company name" />
        </View>
      </View>

      <Label>Industry</Label>
      <ChipRow items={INDUSTRIES} selected={profile.industry} onSelect={(v) => update('industry', v)} color={COLORS.primary} />

      <Label>Experience Level</Label>
      <ChipRow items={EXPERIENCE_LEVELS} selected={profile.experienceLevel} onSelect={(v) => update('experienceLevel', v)} color={COLORS.primary} />

      <Field label="Years of Experience" value={profile.yearsExperience} onChange={(v) => update('yearsExperience', v)} placeholder="e.g. 5" keyboardType="numeric" />
    </>
  );

  const renderExperience = () => (
    <>
      <View style={[s.row, { alignItems: 'center', justifyContent: 'space-between' }]}>
        <SectionTitle>Work Experience ({profile.experiences.length})</SectionTitle>
        <TouchableOpacity style={s.addBtn} onPress={openAddExperience}>
          <MaterialCommunityIcons name="plus" size={16} color="#FFF" />
          <Text style={s.addBtnTxt}>Add</Text>
        </TouchableOpacity>
      </View>

      {profile.experiences.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="briefcase-outline" size={40} color={COLORS.border} />
          <Text style={s.emptyTxt}>No work experience yet</Text>
          <Text style={s.helper}>Add your roles to build out your professional history</Text>
        </View>
      ) : profile.experiences.map(exp => (
        <View key={exp.id} style={s.itemCard}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
            <View style={[s.itemIcon, { backgroundColor: COLORS.primary }]}>
              <MaterialCommunityIcons name="briefcase" size={20} color="#FFF" />
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={s.itemTitle}>{exp.title}</Text>
              <Text style={s.itemSub}>{exp.company}{exp.location ? ` · ${exp.location}` : ''}</Text>
              <Text style={s.itemMeta}>
                {exp.startDate || '—'} → {exp.current ? 'Present' : (exp.endDate || '—')}
              </Text>
              {exp.description && <Text style={s.itemDesc} numberOfLines={3}>{exp.description}</Text>}
            </View>
          </View>
          <View style={s.itemActions}>
            <TouchableOpacity onPress={() => openEditExperience(exp)}>
              <MaterialCommunityIcons name="pencil-outline" size={18} color={COLORS.primary} />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => deleteExperience(exp.id)}>
              <MaterialCommunityIcons name="delete-outline" size={18} color={COLORS.danger} />
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </>
  );

  const renderEducation = () => (
    <>
      <View style={[s.row, { alignItems: 'center', justifyContent: 'space-between' }]}>
        <SectionTitle>Education ({profile.educations.length})</SectionTitle>
        <TouchableOpacity style={s.addBtn} onPress={openAddEducation}>
          <MaterialCommunityIcons name="plus" size={16} color="#FFF" />
          <Text style={s.addBtnTxt}>Add</Text>
        </TouchableOpacity>
      </View>

      {profile.educations.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="school-outline" size={40} color={COLORS.border} />
          <Text style={s.emptyTxt}>No education yet</Text>
        </View>
      ) : profile.educations.map(edu => (
        <View key={edu.id} style={s.itemCard}>
          <View style={{ flexDirection: 'row' }}>
            <View style={[s.itemIcon, { backgroundColor: COLORS.accent }]}>
              <MaterialCommunityIcons name="school" size={20} color="#FFF" />
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={s.itemTitle}>{edu.degree || 'Education'}</Text>
              <Text style={s.itemSub}>{edu.institution}{edu.field ? ` · ${edu.field}` : ''}</Text>
              <Text style={s.itemMeta}>{edu.startDate || '—'} → {edu.endDate || '—'}{edu.gpa ? ` · GPA: ${edu.gpa}` : ''}</Text>
              {edu.description && <Text style={s.itemDesc} numberOfLines={3}>{edu.description}</Text>}
            </View>
          </View>
          <View style={s.itemActions}>
            <TouchableOpacity onPress={() => openEditEducation(edu)}>
              <MaterialCommunityIcons name="pencil-outline" size={18} color={COLORS.primary} />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => deleteEducation(edu.id)}>
              <MaterialCommunityIcons name="delete-outline" size={18} color={COLORS.danger} />
            </TouchableOpacity>
          </View>
        </View>
      ))}

      {/* Certifications */}
      <View style={[s.row, { alignItems: 'center', justifyContent: 'space-between', marginTop: 24 }]}>
        <SectionTitle>Certifications ({profile.certifications.length})</SectionTitle>
        <TouchableOpacity style={s.addBtn} onPress={openAddCert}>
          <MaterialCommunityIcons name="plus" size={16} color="#FFF" />
          <Text style={s.addBtnTxt}>Add</Text>
        </TouchableOpacity>
      </View>

      {profile.certifications.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="certificate-outline" size={40} color={COLORS.border} />
          <Text style={s.emptyTxt}>No certifications yet</Text>
        </View>
      ) : profile.certifications.map(cert => (
        <View key={cert.id} style={s.itemCard}>
          <View style={{ flexDirection: 'row' }}>
            <View style={[s.itemIcon, { backgroundColor: COLORS.warning }]}>
              <MaterialCommunityIcons name="certificate" size={20} color="#FFF" />
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={s.itemTitle}>{cert.name}</Text>
              <Text style={s.itemSub}>{cert.issuer}</Text>
              <Text style={s.itemMeta}>Issued: {cert.issueDate || '—'}{cert.expiryDate ? ` · Expires: ${cert.expiryDate}` : ''}</Text>
              {cert.credentialId && <Text style={s.itemMeta}>ID: {cert.credentialId}</Text>}
            </View>
          </View>
          <View style={s.itemActions}>
            <TouchableOpacity onPress={() => openEditCert(cert)}>
              <MaterialCommunityIcons name="pencil-outline" size={18} color={COLORS.primary} />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => deleteCert(cert.id)}>
              <MaterialCommunityIcons name="delete-outline" size={18} color={COLORS.danger} />
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </>
  );

  const renderSkills = () => (
    <>
      <SectionTitle>Skills ({profile.skills.length})</SectionTitle>
      <View style={s.row}>
        <TextInput
          style={[s.input, { flex: 1, marginRight: 6 }]}
          value={newSkill}
          onChangeText={setNewSkill}
          placeholder="e.g. React Native"
          placeholderTextColor={COLORS.light}
          onSubmitEditing={addSkill}
        />
        <TouchableOpacity style={s.iconBtn} onPress={addSkill}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.primary} />
        </TouchableOpacity>
      </View>

      {profile.skills.map((skill, idx) => (
        <View key={idx} style={s.skillCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Text style={[s.itemTitle, { flex: 1 }]}>{skill.name}</Text>
            <TouchableOpacity onPress={() => removeSkill(idx)}>
              <MaterialCommunityIcons name="close" size={18} color={COLORS.sub} />
            </TouchableOpacity>
          </View>
          <ChipRow items={PROFICIENCY_LEVELS} selected={skill.level} onSelect={(v) => updateSkillLevel(idx, v)} color={COLORS.primary} size="small" />
        </View>
      ))}

      {profile.skills.length === 0 && (
        <View style={s.empty}>
          <MaterialCommunityIcons name="lightbulb-outline" size={40} color={COLORS.border} />
          <Text style={s.emptyTxt}>No skills yet</Text>
        </View>
      )}

      <SectionTitle style={{ marginTop: 24 }}>Languages ({profile.languages.length})</SectionTitle>
      <View style={s.row}>
        <TextInput
          style={[s.input, { flex: 1, marginRight: 6 }]}
          value={newLanguage}
          onChangeText={setNewLanguage}
          placeholder="e.g. English"
          placeholderTextColor={COLORS.light}
          onSubmitEditing={addLanguage}
        />
        <TouchableOpacity style={s.iconBtn} onPress={addLanguage}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.primary} />
        </TouchableOpacity>
      </View>

      {profile.languages.map((lang, idx) => (
        <View key={idx} style={s.skillCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Text style={[s.itemTitle, { flex: 1 }]}>{lang.name}</Text>
            <TouchableOpacity onPress={() => removeLanguage(idx)}>
              <MaterialCommunityIcons name="close" size={18} color={COLORS.sub} />
            </TouchableOpacity>
          </View>
          <ChipRow items={PROFICIENCY_LEVELS} selected={lang.level} onSelect={(v) => updateLanguageLevel(idx, v)} color={COLORS.accent} size="small" />
        </View>
      ))}

      <SectionTitle style={{ marginTop: 24 }}>Interests & Hobbies</SectionTitle>
      <View style={s.row}>
        <TextInput
          style={[s.input, { flex: 1, marginRight: 6 }]}
          value={newInterest}
          onChangeText={setNewInterest}
          placeholder="e.g. Photography, Open source..."
          placeholderTextColor={COLORS.light}
          onSubmitEditing={addInterest}
        />
        <TouchableOpacity style={s.iconBtn} onPress={addInterest}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.primary} />
        </TouchableOpacity>
      </View>
      <View style={s.tagsArea}>
        {profile.interests.map(i => (
          <TouchableOpacity key={i} style={s.tagChip} onPress={() => removeInterest(i)}>
            <Text style={s.tagChipTxt}>{i}</Text>
            <MaterialCommunityIcons name="close-circle" size={14} color={COLORS.sub} />
          </TouchableOpacity>
        ))}
      </View>
    </>
  );

  const renderSocial = () => (
    <>
      <SectionTitle>Social & Online Presence</SectionTitle>
      <SocialField icon="web" label="Website" value={profile.website} onChange={(v) => update('website', v)} placeholder="https://yoursite.com" />
      <SocialField icon="briefcase" label="Portfolio" value={profile.portfolio} onChange={(v) => update('portfolio', v)} placeholder="https://yourportfolio.com" />
      <SocialField icon="linkedin" label="LinkedIn" value={profile.linkedin} onChange={(v) => update('linkedin', v)} placeholder="https://linkedin.com/in/..." />
      <SocialField icon="github" label="GitHub" value={profile.github} onChange={(v) => update('github', v)} placeholder="https://github.com/..." />
      <SocialField icon="twitter" label="Twitter / X" value={profile.twitter} onChange={(v) => update('twitter', v)} placeholder="https://twitter.com/..." />
      <SocialField icon="instagram" label="Instagram" value={profile.instagram} onChange={(v) => update('instagram', v)} placeholder="https://instagram.com/..." />
      <SocialField icon="youtube" label="YouTube" value={profile.youtube} onChange={(v) => update('youtube', v)} placeholder="https://youtube.com/@..." />
      <SocialField icon="palette" label="Behance" value={profile.behance} onChange={(v) => update('behance', v)} placeholder="https://behance.net/..." />
    </>
  );

  const renderPreferences = () => (
    <>
      <SectionTitle>Job Preferences</SectionTitle>

      <View style={s.toggleRow}>
        <View style={{ flex: 1 }}>
          <Text style={s.label}>Open to Work</Text>
          <Text style={s.helper}>Show recruiters you're available</Text>
        </View>
        <Switch
          value={profile.openToWork}
          onValueChange={(v) => update('openToWork', v)}
          trackColor={{ true: COLORS.success, false: COLORS.border }}
          thumbColor="#FFF"
        />
      </View>

      <Label>Job Types (select all that apply)</Label>
      <View style={s.chipsArea}>
        {JOB_TYPES.map(t => (
          <TouchableOpacity
            key={t}
            style={[s.multiChip, profile.jobTypes.includes(t) && { backgroundColor: COLORS.success, borderColor: COLORS.success }]}
            onPress={() => toggleArrayValue('jobTypes', t)}
          >
            <Text style={[s.multiChipTxt, profile.jobTypes.includes(t) && { color: '#FFF', fontWeight: '700' }]}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Label>Work Preference</Label>
      <View style={s.chipsArea}>
        {WORK_PREFERENCES.map(t => (
          <TouchableOpacity
            key={t}
            style={[s.multiChip, profile.workPreferences.includes(t) && { backgroundColor: COLORS.info, borderColor: COLORS.info }]}
            onPress={() => toggleArrayValue('workPreferences', t)}
          >
            <Text style={[s.multiChipTxt, profile.workPreferences.includes(t) && { color: '#FFF', fontWeight: '700' }]}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <SectionTitle style={{ marginTop: 16 }}>Salary Expectations</SectionTitle>
      <Label>Currency</Label>
      <ChipRow items={['GBP', 'USD', 'EUR', 'AED', 'SAR']} selected={profile.salaryCurrency} onSelect={(v) => update('salaryCurrency', v)} color={COLORS.success} />
      <View style={s.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Minimum" value={profile.expectedSalaryMin} onChange={(v) => update('expectedSalaryMin', v)} placeholder="40000" keyboardType="numeric" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Maximum" value={profile.expectedSalaryMax} onChange={(v) => update('expectedSalaryMax', v)} placeholder="60000" keyboardType="numeric" />
        </View>
      </View>

      <Label>Notice Period</Label>
      <ChipRow items={NOTICE_PERIODS} selected={profile.noticePeriod} onSelect={(v) => update('noticePeriod', v)} color={COLORS.primary} />

      <Field label="Availability Note" value={profile.availability} onChange={(v) => update('availability', v)} placeholder="e.g. Available from March 2026" />
      <Field label="Work Authorization" value={profile.workAuthorization} onChange={(v) => update('workAuthorization', v)} placeholder="e.g. UK Citizen, Tier 2 Visa, etc." />

      <SectionTitle style={{ marginTop: 16 }}>Additional</SectionTitle>
      <ToggleRow label="Willing to Relocate" value={profile.willingToRelocate} onChange={(v) => update('willingToRelocate', v)} />
      <ToggleRow label="Willing to Travel" value={profile.willingToTravel} onChange={(v) => update('willingToTravel', v)} />
      <ToggleRow label="Has Driving Licence" value={profile.drivingLicence} onChange={(v) => update('drivingLicence', v)} />

      <SectionTitle style={{ marginTop: 24 }}>Privacy & Visibility</SectionTitle>
      <Label>Profile Visibility</Label>
      <ChipRow items={VISIBILITY_LEVELS} selected={profile.profileVisibility} onSelect={(v) => update('profileVisibility', v)} color={COLORS.danger} />

      <ToggleRow label="Show Email on Profile" value={profile.showEmail} onChange={(v) => update('showEmail', v)} />
      <ToggleRow label="Show Phone on Profile" value={profile.showPhone} onChange={(v) => update('showPhone', v)} />
      <ToggleRow label="Allow Recruiter Contact" value={profile.allowRecruiterContact} onChange={(v) => update('allowRecruiterContact', v)} />
      <ToggleRow label="Allow Direct Messages" value={profile.allowMessages} onChange={(v) => update('allowMessages', v)} />
    </>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modals (Experience, Education, Certification)
  // ══════════════════════════════════════════════════════════════════════════
  const renderExperienceModal = () => (
    <Modal visible={showExpModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowExpModal(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={s.modal}>
          <View style={s.modalH}>
            <TouchableOpacity onPress={() => setShowExpModal(false)}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={s.modalTitle}>{editingExp?.id ? 'Edit Experience' : 'Add Experience'}</Text>
            <TouchableOpacity onPress={saveExperience}>
              <Text style={[s.saveBtn, { color: COLORS.primary }]}>Save</Text>
            </TouchableOpacity>
          </View>
          {editingExp && (
            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              <Field label="Job Title" required value={editingExp.title} onChange={(v) => setEditingExp({ ...editingExp, title: v })} placeholder="e.g. Senior Developer" />
              <Field label="Company" required value={editingExp.company} onChange={(v) => setEditingExp({ ...editingExp, company: v })} placeholder="Company name" />
              <Field label="Location" value={editingExp.location} onChange={(v) => setEditingExp({ ...editingExp, location: v })} placeholder="City, Country" />
              <View style={s.row}>
                <View style={{ flex: 1, marginRight: 6 }}>
                  <Field label="Start Date" value={editingExp.startDate} onChange={(v) => setEditingExp({ ...editingExp, startDate: v })} placeholder="YYYY-MM" />
                </View>
                <View style={{ flex: 1, marginLeft: 6 }}>
                  <Field label="End Date" value={editingExp.endDate} onChange={(v) => setEditingExp({ ...editingExp, endDate: v })} placeholder="YYYY-MM" />
                </View>
              </View>
              <View style={s.toggleRow}>
                <Text style={s.label}>I currently work here</Text>
                <Switch value={editingExp.current} onValueChange={(v) => setEditingExp({ ...editingExp, current: v, endDate: v ? '' : editingExp.endDate })} trackColor={{ true: COLORS.primary, false: COLORS.border }} thumbColor="#FFF" />
              </View>
              <Field label="Description" value={editingExp.description} onChange={(v) => setEditingExp({ ...editingExp, description: v })} placeholder="Key responsibilities, achievements, technologies..." multiline rows={6} />
            </ScrollView>
          )}
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderEducationModal = () => (
    <Modal visible={showEduModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowEduModal(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={s.modal}>
          <View style={s.modalH}>
            <TouchableOpacity onPress={() => setShowEduModal(false)}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={s.modalTitle}>{editingEdu?.id ? 'Edit Education' : 'Add Education'}</Text>
            <TouchableOpacity onPress={saveEducation}>
              <Text style={[s.saveBtn, { color: COLORS.accent }]}>Save</Text>
            </TouchableOpacity>
          </View>
          {editingEdu && (
            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              <Field label="Institution" required value={editingEdu.institution} onChange={(v) => setEditingEdu({ ...editingEdu, institution: v })} placeholder="University name" />
              <Field label="Degree" value={editingEdu.degree} onChange={(v) => setEditingEdu({ ...editingEdu, degree: v })} placeholder="e.g. BSc Computer Science" />
              <Field label="Field of Study" value={editingEdu.field} onChange={(v) => setEditingEdu({ ...editingEdu, field: v })} placeholder="e.g. Computer Science" />
              <View style={s.row}>
                <View style={{ flex: 1, marginRight: 6 }}>
                  <Field label="Start Date" value={editingEdu.startDate} onChange={(v) => setEditingEdu({ ...editingEdu, startDate: v })} placeholder="YYYY" />
                </View>
                <View style={{ flex: 1, marginLeft: 6 }}>
                  <Field label="End Date" value={editingEdu.endDate} onChange={(v) => setEditingEdu({ ...editingEdu, endDate: v })} placeholder="YYYY" />
                </View>
              </View>
              <Field label="GPA / Grade" value={editingEdu.gpa} onChange={(v) => setEditingEdu({ ...editingEdu, gpa: v })} placeholder="e.g. First Class, 3.8/4.0" />
              <Field label="Description" value={editingEdu.description} onChange={(v) => setEditingEdu({ ...editingEdu, description: v })} placeholder="Coursework, activities, awards..." multiline rows={4} />
            </ScrollView>
          )}
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderCertModal = () => (
    <Modal visible={showCertModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowCertModal(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={s.modal}>
          <View style={s.modalH}>
            <TouchableOpacity onPress={() => setShowCertModal(false)}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={s.modalTitle}>{editingCert?.id ? 'Edit Certification' : 'Add Certification'}</Text>
            <TouchableOpacity onPress={saveCert}>
              <Text style={[s.saveBtn, { color: COLORS.warning }]}>Save</Text>
            </TouchableOpacity>
          </View>
          {editingCert && (
            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              <Field label="Certification Name" required value={editingCert.name} onChange={(v) => setEditingCert({ ...editingCert, name: v })} placeholder="e.g. AWS Solutions Architect" />
              <Field label="Issuing Organization" value={editingCert.issuer} onChange={(v) => setEditingCert({ ...editingCert, issuer: v })} placeholder="e.g. Amazon Web Services" />
              <View style={s.row}>
                <View style={{ flex: 1, marginRight: 6 }}>
                  <Field label="Issue Date" value={editingCert.issueDate} onChange={(v) => setEditingCert({ ...editingCert, issueDate: v })} placeholder="YYYY-MM" />
                </View>
                <View style={{ flex: 1, marginLeft: 6 }}>
                  <Field label="Expiry Date" value={editingCert.expiryDate} onChange={(v) => setEditingCert({ ...editingCert, expiryDate: v })} placeholder="YYYY-MM" />
                </View>
              </View>
              <Field label="Credential ID" value={editingCert.credentialId} onChange={(v) => setEditingCert({ ...editingCert, credentialId: v })} placeholder="Credential number" />
            </ScrollView>
          )}
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Section nav
  // ══════════════════════════════════════════════════════════════════════════
  const sections = [
    { id: 'basic',         label: 'Basic',         icon: 'account' },
    { id: 'professional',  label: 'Professional',  icon: 'briefcase' },
    { id: 'experience',    label: 'Experience',    icon: 'briefcase-clock' },
    { id: 'education',     label: 'Education',     icon: 'school' },
    { id: 'skills',        label: 'Skills',        icon: 'lightbulb' },
    { id: 'social',        label: 'Social',        icon: 'web' },
    { id: 'preferences',   label: 'Preferences',   icon: 'cog' },
  ];

  if (loading) {
    return (
      <SafeAreaView style={s.loading}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={s.loadingTxt}>Loading profile...</Text>
      </SafeAreaView>
    );
  }

  return (
    <KeyboardAvoidingView style={s.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primary} />

      {/* Header */}
      <LinearGradient colors={[COLORS.primary, COLORS.primaryDk]} style={s.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TouchableOpacity style={s.hBtn} onPress={handleBack}>
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={s.hTitle}>Edit Profile</Text>
            <Text style={s.hSub}>{completion}% complete{dirty ? ' · Unsaved changes' : ''}</Text>
          </View>
          <TouchableOpacity onPress={handleSave} disabled={saving || !dirty} style={[s.saveHeaderBtn, !dirty && { opacity: 0.4 }]}>
            {saving ? <ActivityIndicator color="#FFF" /> : <Text style={s.saveHeaderTxt}>Save</Text>}
          </TouchableOpacity>
        </View>

        {/* Completion bar */}
        <View style={s.completionBar}>
          <View style={[s.completionFill, { width: `${completion}%` }]} />
        </View>
      </LinearGradient>

      {/* Section tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.sectionTabs} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {sections.map(sec => (
          <TouchableOpacity
            key={sec.id}
            style={[s.sectionTab, activeSection === sec.id && { backgroundColor: `${COLORS.primary}15` }]}
            onPress={() => setActiveSection(sec.id)}
          >
            <MaterialCommunityIcons name={sec.icon} size={16} color={activeSection === sec.id ? COLORS.primary : COLORS.sub} />
            <Text style={[s.sectionTabTxt, activeSection === sec.id && { color: COLORS.primary, fontWeight: '700' }]}>{sec.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Body */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 100 }} keyboardShouldPersistTaps="handled">
        {activeSection === 'basic'        && renderBasic()}
        {activeSection === 'professional' && renderProfessional()}
        {activeSection === 'experience'   && renderExperience()}
        {activeSection === 'education'    && renderEducation()}
        {activeSection === 'skills'       && renderSkills()}
        {activeSection === 'social'       && renderSocial()}
        {activeSection === 'preferences'  && renderPreferences()}
      </ScrollView>

      {/* Floating save button (large) */}
      {dirty && (
        <TouchableOpacity style={s.floatingSave} onPress={handleSave} disabled={saving}>
          {saving ? <ActivityIndicator color="#FFF" /> : (
            <>
              <MaterialCommunityIcons name="content-save" size={20} color="#FFF" />
              <Text style={s.floatingSaveTxt}>Save Changes</Text>
            </>
          )}
        </TouchableOpacity>
      )}

      {renderExperienceModal()}
      {renderEducationModal()}
      {renderCertModal()}
    </KeyboardAvoidingView>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Reusable components
// ══════════════════════════════════════════════════════════════════════════════
const Field = ({ label, required, value, onChange, placeholder, multiline, rows, keyboardType, autoCapitalize, maxLength }) => (
  <View style={{ marginBottom: 14 }}>
    {label && (
      <Text style={s.label}>
        {label}{required && <Text style={{ color: COLORS.danger }}> *</Text>}
      </Text>
    )}
    <TextInput
      style={[s.input, multiline && { minHeight: 28 * (rows || 4), textAlignVertical: 'top' }]}
      value={String(value || '')}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={COLORS.light}
      multiline={multiline}
      keyboardType={keyboardType}
      autoCapitalize={autoCapitalize}
      maxLength={maxLength}
    />
  </View>
);

const SocialField = ({ icon, label, value, onChange, placeholder }) => (
  <View style={s.socialRow}>
    <View style={s.socialIconWrap}>
      <MaterialCommunityIcons name={icon} size={20} color={COLORS.primary} />
    </View>
    <View style={{ flex: 1 }}>
      <Text style={s.label}>{label}</Text>
      <TextInput
        style={s.input}
        value={String(value || '')}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={COLORS.light}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="url"
      />
    </View>
  </View>
);

const Label = ({ children, style }) => <Text style={[s.label, { marginTop: 4, marginBottom: 6 }, style]}>{children}</Text>;

const SectionTitle = ({ children, style }) => <Text style={[s.sectionTitle, style]}>{children}</Text>;

const ChipRow = ({ items, selected, onSelect, color, size }) => (
  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingVertical: 4 }}>
    {items.map(item => (
      <TouchableOpacity
        key={item}
        style={[s.chip, size === 'small' && s.chipSmall, selected === item && { backgroundColor: color, borderColor: color }]}
        onPress={() => onSelect(item)}
      >
        <Text style={[s.chipTxt, size === 'small' && { fontSize: 11 }, selected === item && { color: '#FFF', fontWeight: '700' }]}>{item}</Text>
      </TouchableOpacity>
    ))}
  </ScrollView>
);

const ToggleRow = ({ label, value, onChange, helper }) => (
  <View style={s.toggleRow}>
    <View style={{ flex: 1 }}>
      <Text style={s.label}>{label}</Text>
      {helper && <Text style={s.helper}>{helper}</Text>}
    </View>
    <Switch value={value} onValueChange={onChange} trackColor={{ true: COLORS.primary, false: COLORS.border }} thumbColor="#FFF" />
  </View>
);

// ══════════════════════════════════════════════════════════════════════════════
// Styles
// ══════════════════════════════════════════════════════════════════════════════
const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg },
  loadingTxt: { fontSize: 14, color: COLORS.sub, marginTop: 12 },

  // Header
  header: { paddingTop: 50, paddingBottom: 12, paddingHorizontal: 16 },
  hBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 20, fontWeight: '700', color: '#FFF' },
  hSub: { fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 },
  saveHeaderBtn: { backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 16 },
  saveHeaderTxt: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  completionBar: { height: 4, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 2, marginTop: 14, overflow: 'hidden' },
  completionFill: { height: '100%', backgroundColor: COLORS.success },

  // Section tabs
  sectionTabs: { flexGrow: 0, flexShrink: 0, backgroundColor: COLORS.surface, borderBottomWidth: 1, borderBottomColor: COLORS.border, paddingVertical: 8 },
  sectionTab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, marginRight: 6, borderRadius: 16, gap: 6 },
  sectionTabTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },

  // Section content
  sectionTitle: { fontSize: 15, fontWeight: '700', color: COLORS.text, marginBottom: 12, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  label: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginBottom: 6 },
  helper: { fontSize: 11, color: COLORS.sub, marginTop: 4 },
  input: { backgroundColor: COLORS.surface, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: COLORS.text, borderWidth: 1, borderColor: COLORS.border },
  row: { flexDirection: 'row' },

  // Photo
  photoRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  profilePhoto: { width: 100, height: 100, borderRadius: 50, backgroundColor: COLORS.bgAlt, borderWidth: 2, borderColor: COLORS.border, justifyContent: 'center', alignItems: 'center', overflow: 'hidden' },

  // Chips
  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.surface, marginRight: 6 },
  chipSmall: { paddingHorizontal: 10, paddingVertical: 5 },
  chipTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },
  chipsArea: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  multiChip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.surface, marginRight: 6, marginBottom: 6 },
  multiChipTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },

  // Tag chips
  tagsArea: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  tagChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: `${COLORS.primary}15`, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 14, gap: 4 },
  tagChipTxt: { fontSize: 12, color: COLORS.primary, fontWeight: '600' },

  // Item cards (exp, edu, cert)
  itemCard: { backgroundColor: COLORS.surface, borderRadius: 12, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: COLORS.border },
  itemIcon: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  itemTitle: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  itemSub: { fontSize: 13, color: COLORS.sub, marginTop: 2 },
  itemMeta: { fontSize: 11, color: COLORS.light, marginTop: 4 },
  itemDesc: { fontSize: 12, color: COLORS.sub, marginTop: 6, lineHeight: 17 },
  itemActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: COLORS.bgAlt },

  // Skill cards
  skillCard: { backgroundColor: COLORS.surface, borderRadius: 10, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: COLORS.border },

  // Social rows
  socialRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 10 },
  socialIconWrap: { width: 40, height: 40, borderRadius: 20, backgroundColor: `${COLORS.primary}15`, justifyContent: 'center', alignItems: 'center', marginTop: 22 },

  // Empty
  empty: { alignItems: 'center', padding: 24, backgroundColor: COLORS.surface, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border, borderStyle: 'dashed', marginVertical: 8 },
  emptyTxt: { fontSize: 14, fontWeight: '600', color: COLORS.text, marginTop: 8 },

  // Buttons
  addBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.primary, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, gap: 4 },
  addBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 12 },
  iconBtn: { width: 44, height: 44, borderRadius: 10, backgroundColor: COLORS.surface, borderWidth: 1, borderColor: COLORS.border, justifyContent: 'center', alignItems: 'center' },

  // Toggle row
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: COLORS.surface, borderRadius: 10, padding: 14, marginBottom: 8, borderWidth: 1, borderColor: COLORS.border },

  // Floating save
  floatingSave: { position: 'absolute', bottom: 20, alignSelf: 'center', flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.success, paddingHorizontal: 24, paddingVertical: 14, borderRadius: 28, gap: 8, elevation: 6, shadowColor: '#000', shadowOpacity: 0.25, shadowOffset: { width: 0, height: 4 }, shadowRadius: 8 },
  floatingSaveTxt: { color: '#FFF', fontWeight: '700', fontSize: 15 },

  // Modal
  modal: { flex: 1, backgroundColor: COLORS.surface },
  modalH: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  modalTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1, marginHorizontal: 12 },
  saveBtn: { fontSize: 16, fontWeight: '700' },
});

export default EditProfileScreen;